import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  ChangeEvent,
  SetStateAction,
  Dispatch,
} from "react";
import Drawer from "react-modern-drawer";
import { useLoads } from "react-loads";
import debounce from "lodash.debounce";
import { isAnathoth } from "@util/constants";

import { useStore, defaultShopify } from "@state/store";
import { Container, H2, P } from "@util/standard";
import { IconButton, Button, Image, Accordion } from "@global";
import styled from "styled-components";
import { colorsRGB, colors, MOBILE_BREAKPOINT } from "@util/constants";
import { moneyFormat, useDebounce } from "@util/helper";
import QuantityInput from "@shared/quantityInput";
import { Shopify } from "@state/types";
import { CartLineItem } from "@util/types";
import { navigate } from "gatsby-link";
import { Attribute } from "shopify-storefront-api-typings";
import { useCheckScreenWidth } from "@util/hooks";
import { toast } from "react-toastify";

const StyledDrawer = styled(Drawer)`
  .Accordion__header {
    font-weight: bold;
  }
`;

const CartContainer = styled(Container)`
  padding: 70px 30px 20px;
`;

const CloseContainer = styled(Container)`
  position: absolute;
  top: 20px;
  right: 15px;
`;

const FooterContainer = styled(Container)`
  border-top: 1px solid ${colorsRGB.merlin(0.35)};
  padding-top: 15px;

  #dispatch-date {
    cursor: pointer;
  }

  .Accordion {
    margin: 17px 0;
    border-top: 0;
    border-radius: 0px !important;
    box-sizing: border-box;
    &:not(:last-of-type) {
      border-bottom: 1px solid ${colorsRGB.merlin(0.35)};
      padding-bottom: 10px;
    }
  }
  .Accordion__header {
    padding: 0;
    font-weight: 400;
    font-size: 16px;
    width: 100%;
    // justify-content: center;
    &:after {
      // position: relative;
      // margin: 0 0 0 10px;
      // left: initial;
      // right: initial;
      width: 10px;
      height: 10px;
    }
  }
  .Accordion__body:not(.Accordion__body--collapsed) {
    padding: 10px 0 0;
  }
`;

const LineItemContainer = styled(Container)<{
  lastItem?: boolean;
}>`
  padding: 0 0 30px;
  ${({ lastItem }) =>
    lastItem === false &&
    `
    margin: 0 0 30px;
    border-bottom: 1px solid ${colorsRGB.merlin(0.35)};
  `}
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 0 20px;
    ${({ lastItem }) =>
      lastItem === false &&
      `
      margin: 0 0 20px;
    `}
  }
`;

const ImageContainer = styled(Container)<{ customBackgroundColour?: string }>`
  align-items: center;
  background: ${colors.imageBackground};
  ${({ customBackgroundColour }) =>
    customBackgroundColour && `background: ${customBackgroundColour};`}
  padding: 20px 22px;
  width: 112px;
  img {
    width: 68px;
    height: 108px;
    object-fit: contain;
    opacity: 1;
    transition: opacity 0.3s ease;
    &:hover {
      opacity: 0.5;
    }
  }
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 10px 12px;
    width: 73px;
    img {
      width: 49px;
      height: 77px;
    }
  }
`;

const MetaContainer = styled(Container)`
  width: calc(100% - 112px - 104px);
  p {
    margin: 0 0 5px;
  }
  span {
    color: ${colorsRGB.black(0.6)};
  }
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: calc(100% - 73px - 104px);
  }
`;

const SaleSpan = styled.span`
  && {
    color: ${colorsRGB.black(0.4)};
    text-decoration: line-through;
    margin-left: 10px;
  }
`;

const SmallParagraph = styled.p`
  && {
    margin: 12px 0 0;
    font-size: 12px;
    color: ${colorsRGB.black(0.6)};
  }
`;

const VariantParagraph = styled.p`
  && {
    margin: 0 0 10px;
    font-size: 13px;
    color: ${colorsRGB.black(0.65)};
  }
`;

const DeleteBtn = styled.a<{ disabled?: boolean }>`
  text-decoration: underline;
  transition: 0.3s all ease;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `};
  &:hover,
  &:active,
  &:focus {
    text-shadow: none;
    opacity: 0.5;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  line-height: 1.6;
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid ${colorsRGB.merlin(0.35)};
  color: var(--theme-primary-color);
  font-size: 16px;
`;

const StyledA = styled.a`
  text-decoration: underline;
`;

const Label = styled.label`
  cursor: pointer;
`;

const Checkbox = styled.input`
  cursor: pointer;
  margin-right: 5px;
`;

interface Props {
  visible: boolean;
}
interface HandleProps {
  lineItems: CartLineItem[];
  shopify: Shopify;
}
interface PostNoteUpdateProps {
  note: string;
  shopify: Shopify;
  setNoteUpdating: React.Dispatch<React.SetStateAction<boolean>>;
}

interface PostCartAttributesUpdateProps {
  attributes: Attribute[];
  shopify: Shopify;
  setNoteUpdating: React.Dispatch<React.SetStateAction<boolean>>;
}

const postNoteUpdate = async ({
  note,
  shopify,
  setNoteUpdating,
}: PostNoteUpdateProps) => {
  const data = {
    note: note,
    cartId: shopify?.cart?.id,
  };
  if (!shopify?.cart?.id || !note) return;
  const res = await fetch(`/api/cartNoteUpdate`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  try {
    const response = await res.json();
    console.log({ response });

    if (response.error?.length) {
      if (response?.error?.[0]?.field?.[0] === "cartId") {
        useStore.setState({
          shopify: {
            ...shopify,
            cart: defaultShopify.cart,
          },
        });
        setNoteUpdating(false);
        return;
      }

      throw new Error(response.error);
    } else {
      useStore.setState({
        shopify: {
          ...shopify,
          cart: response.cart,
        },
      });
    }
    setNoteUpdating(false);
  } catch (err) {
    setNoteUpdating(false);
    throw err;
  }
};

const postCartAttributesUpdate = async ({
  attributes,
  shopify,
  setNoteUpdating,
}: PostCartAttributesUpdateProps) => {
  const data = {
    attributes: attributes,
    cartId: shopify?.cart?.id,
  };

  if (!shopify?.cart?.id) return;
  const res = await fetch(`/api/cartAttributesUpdate`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  try {
    const response = await res.json();
    // console.log({ response });

    if (response.error?.length) {
      if (response?.error?.[0]?.field?.[0] === "cartId") {
        useStore.setState({
          shopify: {
            ...shopify,
            cart: defaultShopify.cart,
          },
        });
        setNoteUpdating(false);
        return;
      }

      throw new Error(response.error);
    } else {
      useStore.setState({
        shopify: {
          ...shopify,
          cart: response.cart,
        },
      });
    }
    setNoteUpdating(false);
  } catch (err) {
    setNoteUpdating(false);
    throw err;
  }
};

const Cart = ({ visible }: Props) => {
  const { closeDrawer, shopify } = useStore();
  const { isMobileWidth } = useCheckScreenWidth();
  const elCartContainer = useRef<HTMLDivElement>(null);
  const elHeader = useRef<HTMLDivElement>(null);
  const elFooter = useRef<HTMLDivElement>(null);
  const [cartHeight, setCartHeight] = useState<string>("auto");
  const [adding, setAdding] = useState(false);
  const cart = shopify?.cart;
  const [lineItems, setLineItems] = useState<CartLineItem[]>([]);
  const defaultDeliveryNote = cart?.note ?? "";
  const [deliveryNote, setDeliveryNote] = useState(defaultDeliveryNote);
  const defaultGiftMessage =
    cart?.attributes?.find(attr => attr.key === "gift-message")?.value ?? "";
  const [giftMessage, setGiftMessage] = useState(defaultGiftMessage);
  const debouncedDeliveryNote = useDebounce(deliveryNote, 2000);
  const debouncedGiftMessage = useDebounce(giftMessage, 2000);
  const [noteUpdating, setNoteUpdating] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dispatchDate, setDispatchDate] = useState<string>();
  const [dispatchDateError, setDispatchDateError] = useState<string | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClick = () => {
    inputRef.current?.showPicker?.();
  };

  useEffect(() => {
    if (debouncedGiftMessage == null) return;
    if (cart?.id == null) return;

    const arg = {
      attributes: [
        {
          key: "dispatch-date",
          value: dispatchDate == null ? "Not Specified" : dispatchDate,
        },
        {
          key: "gift-message",
          value:
            giftMessage == null || giftMessage === "" ? "N/A" : giftMessage,
        },
      ],
      shopify: shopify,
      setNoteUpdating: setNoteUpdating,
    };

    postCartAttributesUpdate(arg);
  }, [debouncedGiftMessage]);

  useEffect(() => {
    if (debouncedDeliveryNote == null) return;
    if (cart?.id == null) return;

    const arg = {
      note: deliveryNote,
      shopify: shopify,
      setNoteUpdating: setNoteUpdating,
    };
    postNoteUpdate(arg);
  }, [debouncedDeliveryNote]);

  useEffect(() => {
    const setCartContainerHeight = () => {
      const conHeight = elCartContainer?.current?.offsetHeight;
      const headerHeight = elHeader?.current?.offsetHeight;
      const footerHeight = elFooter?.current?.offsetHeight;
      if (!conHeight || !headerHeight || !footerHeight) return;
      let height = conHeight - headerHeight - footerHeight;
      setCartHeight(height + "px");
    };
    setCartContainerHeight();
  }, []);

  const handleLineItemsUpdate = useCallback(async (arg: HandleProps) => {
    if (lineItems.length === 0) return;
    setAdding(true);

    const data = {
      lines: arg.lineItems,
      cartId: arg.shopify?.cart?.id,
    };

    const res = await fetch(`/api/cartLinesUpdate`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    try {
      const response = await res.json();
      if (response.error?.length) {
        throw new Error(response.error);
      } else {
        useStore.setState({
          shopify: {
            ...arg.shopify,
            cart: response.cart,
          },
        });
      }
      setAdding(false);
      setLineItems([]);
    } catch (err) {
      setAdding(false);
      setLineItems([]);
      throw err;
    }
  }, []);

  const { load } = useLoads("handleLineItemsUpdate", handleLineItemsUpdate);

  const debouncedHandleLineItemsUpdate = useCallback(
    debounce((arg: HandleProps) => load(arg), 500),
    [],
  );

  const updateLineItem = (id?: string, quantity?: number, change?: number) => {
    if (id === undefined || quantity === undefined || change === undefined) {
      return;
    }
    let newArr: CartLineItem[] = lineItems,
      isExist = false;

    newArr.forEach((lineItem, i) => {
      if (lineItem.id === id) {
        newArr[i].quantity += change;
        isExist = true;
      }
    });
    if (!isExist) {
      newArr.push({ id: id, quantity: (quantity += change) });
    }
    setLineItems(newArr);
    const arg: HandleProps = {
      lineItems,
      shopify: shopify,
    };
    debouncedHandleLineItemsUpdate(arg);
  };

  const inputChanged = (e: HTMLInputElement) => {
    const accepted = e.target.checked;

    setTermsAccepted(accepted);
  };

  const calendarDateChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;

    if (!date) {
      setDispatchDateError("Please select a date.");
      return;
    }

    const d = new Date(date);
    if (isNaN(d.getTime())) {
      setDispatchDateError("Invalid date.");
      return;
    }

    const year = d.getFullYear();
    if (year < 2024 || year > 2100) {
      setDispatchDateError("Year must be between 2024 and 2100.");
      return;
    }

    const day = d.getDay();
    if (day === 0 || day === 6) {
      setDispatchDateError("Please choose a weekday (Mon–Fri).");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    if (d <= today) {
      setDispatchDateError("Date must be in the future.");
      return;
    }

    setDispatchDateError(null);
    setDispatchDate(date);

    postCartAttributesUpdate({
      attributes: [
        { key: "dispatch-date", value: date },
        { key: "gift-message", value: giftMessage.trim() || "[empty]" },
      ],
      shopify,
      setNoteUpdating,
    });
  };

  const goToCheckout = () => {
    //Submit delivery note
    if (!shopify?.cart?.checkoutUrl) return;
    if (!termsAccepted) {
      toast.error("Please accept the terms and conditions to continue");
      return;
    }
    const customer = shopify.customer;
    const { firstName, lastName, email, defaultAddress } = customer;

    if (customer == null || !email) {
      window.location = shopify.cart.checkoutUrl;
      return;
    }

    const paramArr = [
      firstName &&
        "checkout[shipping_address][first_name]=" + encodeURI(firstName),
      lastName &&
        "checkout[shipping_address][last_name]=" + encodeURI(lastName),
      email && "checkout[email]=" + email,
      defaultAddress?.address1 &&
        "checkout[shipping_address][address1]=" +
          encodeURI(defaultAddress.address1),
      defaultAddress?.address2 &&
        "checkout[shipping_address][address2]=" +
          encodeURI(defaultAddress.address2),
      defaultAddress?.city &&
        "checkout[shipping_address][city]=" + encodeURI(defaultAddress.city),
      defaultAddress?.zip &&
        "checkout[shipping_address][zip]=" + encodeURI(defaultAddress.zip),
    ];

    const newCheckoutUrl = `${shopify.cart.checkoutUrl}?${paramArr.join("&")}`;
    window.location = newCheckoutUrl;
  };

  const handleNoteChange = (
    event: ChangeEvent,
    setFunc: Dispatch<SetStateAction<string>>,
  ) => {
    const target = event.target;
    const value = target.value;

    setNoteUpdating(true);
    value != null && setFunc(value);
  };

  return (
    <StyledDrawer
      open={visible}
      onClose={closeDrawer}
      direction="right"
      style={{
        width: isMobileWidth ? "100%" : "35%",
        height: "100%",
        overflow: "auto",
      }}
    >
      <CloseContainer>
        <IconButton icon="close" onClick={closeDrawer} />
      </CloseContainer>

      <CartContainer ref={elCartContainer} height="100%" flexDirection="column">
        {/* Header */}
        <Container ref={elHeader} flexDirection="column">
          <H2 margin="0 0 14px">Cart</H2>
          {cart?.lines?.edges && cart.lines.edges.length > 0 ? (
            <>
              <P margin="0">Taxes and shipping are calculated at checkout.</P>
              <P margin="0">Discounts are calculated at checkout.</P>
            </>
          ) : (
            <P>Your cart is empty.</P>
          )}
        </Container>

        {/* Body */}
        <Container margin="30px 0 0">
          <Container padding="0 0 35px" flexDirection="column" width="100%">
            {cart?.lines?.edges &&
              cart.lines.edges.length > 0 &&
              cart.lines.edges.map((line, index) => {
                const lineItem = line?.node?.merchandise;
                const lastItem = cart?.lines?.edges?.length === index + 1;
                const attributes = line?.node?.attributes;
                const variantTitle = lineItem?.title;
                const selectedOptions = lineItem?.selectedOptions ?? [];
                const selectedVariantLabel =
                  selectedOptions.length > 0
                    ? selectedOptions
                        .map(
                          option =>
                            option?.value ||
                            (option?.name && option?.value
                              ? `${option.name}: ${option.value}`
                              : option?.name),
                        )
                        .filter(Boolean)
                        .join(" / ")
                    : variantTitle && variantTitle !== "Default Title"
                      ? variantTitle
                      : "";

                if (!line?.node) return;
                if (!lineItem) return;
                if (!line.node.quantity) return;
                return (
                  <LineItemContainer
                    lastItem={lastItem}
                    key={line.node.id}
                    alignItems="stretch"
                  >
                    <ImageContainer
                      customBackgroundColour={colors.lightGrayBackground}
                    >
                      <Image
                        onClick={() =>
                          navigate(`/shop/${lineItem.product?.handle}`)
                        }
                        staticImage={lineItem.image?.transformedSrc}
                        altText={lineItem.product?.title}
                      />
                    </ImageContainer>
                    <MetaContainer padding="0 20px" flexDirection="column">
                      <p>{lineItem.product?.title}</p>
                      {selectedVariantLabel && (
                        <VariantParagraph>
                          {selectedVariantLabel}
                        </VariantParagraph>
                      )}
                      <Container flexWrap="wrap">
                        <span>
                          {moneyFormat(
                            lineItem.priceV2?.amount * line.node.quantity,
                          )}
                        </span>
                        {lineItem.compareAtPriceV2 && (
                          <SaleSpan>
                            {moneyFormat(
                              lineItem.compareAtPriceV2.amount *
                                line.node.quantity,
                            )}
                          </SaleSpan>
                        )}
                      </Container>
                      <SmallParagraph>
                        Unit Price: {moneyFormat(lineItem.priceV2?.amount)}
                      </SmallParagraph>
                      {attributes && (
                        <div>
                          {attributes.map(attr => {
                            if (attr.value?.includes("http")) return null;
                            return <pre key={attr.key}>{attr.value}</pre>;
                          })}
                        </div>
                      )}
                    </MetaContainer>
                    <Container
                      flexDirection="column"
                      justifyContent="space-between"
                      alignItems="flex-end"
                    >
                      <QuantityInput
                        fontSize="12px"
                        defaultQuantity={line.node.quantity}
                        updateLineItemFunc={updateLineItem}
                        lineItemId={line.node.id}
                        resetOnShopifyUpdate={true}
                        disabled={adding}
                      />
                      <input
                        type="hidden"
                        name={`lineId-${index}`}
                        value={line.node.id}
                      ></input>
                      <DeleteBtn
                        disabled={adding}
                        onClick={() =>
                          updateLineItem(
                            line.node?.id,
                            line.node?.quantity,
                            line.node?.quantity && -line.node.quantity,
                          )
                        }
                      >
                        Delete
                      </DeleteBtn>
                    </Container>
                  </LineItemContainer>
                );
              })}
          </Container>
        </Container>

        {/* Footer */}
        {shopify?.cart?.id && (
          <FooterContainer ref={elFooter} flexDirection="column">
            <Container justifyContent="space-between">
              <P>Subtotal</P>
              <P>
                {moneyFormat(cart?.estimatedCost?.subtotalAmount?.amount ?? 0)}
              </P>
            </Container>

            <p>Add Gift Message</p>
            <Textarea
              rows={3}
              name="giftMessage"
              defaultValue={
                defaultGiftMessage !== "[empty]" ? defaultGiftMessage : ""
              }
              onChange={e => handleNoteChange(e, setGiftMessage)}
            ></Textarea>
            <p>Add Delivery Notes</p>
            <Textarea
              rows={3}
              name="notes"
              defaultValue={defaultDeliveryNote}
              onChange={e => handleNoteChange(e, setDeliveryNote)}
            ></Textarea>
            <Accordion title="Dispatch date" noBorder normal={false}>
              <Container flexDirection="column" margin="20px 0px">
                <p style={{ margin: "0px 0px 20px 0px" }}>
                  Set up a preferred dispatch date. We will try our best to get
                  it sent on this day. This is not the delivery date. Please
                  note, we do not dispatch orders on weekends or public holidays
                </p>
                {dispatchDateError && (
                  <p style={{ color: "red" }}>{dispatchDateError}</p>
                )}
                <input
                  ref={inputRef}
                  id="dispatch-date"
                  type="date"
                  onChange={calendarDateChanged}
                  value={dispatchDate}
                  onKeyDown={e => e.preventDefault()}
                  onClick={handleClick}
                />
              </Container>
            </Accordion>
            <Container margin="0px 0px 20px 0px">
              <form>
                <Checkbox
                  id="checkbox"
                  type="checkbox"
                  required
                  onChange={inputChanged}
                ></Checkbox>
                <Label for="checkbox">
                  I agree to the{" "}
                  <StyledA
                    href={
                      isAnathoth ? "/terms-and-conditions" : "/terms-conditions"
                    }
                    target="_blank"
                  >
                    T&Cs
                  </StyledA>
                </Label>
              </form>
            </Container>

            <Button
              margin="0 0 10px"
              disabled={adding || noteUpdating}
              theme="base"
              text={noteUpdating ? "UPDATING NOTE" : "CHECKOUT"}
              onClick={goToCheckout}
            />
          </FooterContainer>
        )}
      </CartContainer>
    </StyledDrawer>
  );
};

export default Cart;
